import React, { useMemo, useRef, useState } from 'react';
import { ToggleButtons } from '@components/ToggleButtons';
import config from '@config/index';
import { useOrganization } from '@context/OrganizationContext';
import {
  useSubscriptions,
  useSubscriptionsDispatch,
} from '@features/subscriptions/context/subscription-context';
import { useUncancelSubscription } from '@features/subscriptions/hooks/useUncancelSubscription';
import type { DrawerRefMethods } from '@goodfynd/hooks';
import { Button, FlexView, Loading, Tag } from '@goodfynd/ui';
import { addClassNames } from '@goodfynd/theme';
import { Link, Text } from '@goodfynd/ui';
import { format } from '@utils/date-util';
import { renderPrice } from '@utils/number-util';
import { join } from '@utils/string-util';
import type { SubscriptionFrequency } from 'types/account/subscriptions';

import { SuiteCheckoutDrawer } from '../subscriptions-checkout/SuiteCheckoutDrawer';

import { ChangePaymentMethodFormDrawer } from './ChangePaymentMethodFormDrawer';
import type { ActiveSuiteProps } from './types';

import classes from './ActiveSuite.module.css';

export const ActiveSuite = ({ className }: ActiveSuiteProps) => {
  const [showPlanOptions, togglePlanOptions] = useState(false);

  const {
    activeSubscriptions: subscriptions,
    activeSuite,
    cart,
    defaultPaymentMethod,
    subscriptionItems,
    suites,
  } = useSubscriptions();
  const { changeSuite, resetSubscriptions, setFrequency } =
    useSubscriptionsDispatch();

  const { organizationId } = useOrganization();
  const { uncancelSubscription, isUncancelingSubscription } =
    useUncancelSubscription(organizationId, {
      onSuccess() {
        changeSuite(undefined);
        resetSubscriptions();
      },
    });

  const changePaymentMethodDrawerRef = useRef<DrawerRefMethods>(null);

  const nextPlanDue = useMemo(() => {
    return subscriptions
      .filter((subscription) =>
        subscription.items.find((item) =>
          item.product.categories?.includes('suite')
        )
      )
      .sort(
        (a, b) =>
          new Date(a.items[0].currentPeriodEnd).getTime() -
          new Date(b.items[0].currentPeriodEnd).getTime()
      )[0];
  }, [subscriptions]);
  const hasPhases = useMemo(() => {
    return !!nextPlanDue?.phases?.[0];
  }, [nextPlanDue]);

  const suiteNamesDisplay = useMemo(() => {
    return activeSuite?.product.name ?? 'Not yet subscribed';
  }, [activeSuite?.product.name]);

  const suiteTotalDisplay = useMemo(() => {
    const total = activeSuite?.price.price ?? 0;
    if (!total) {
      return 'FREE';
    }

    if (cart.frequency === 'annual') {
      return `${renderPrice(total)}/year`;
    }

    return `${renderPrice(total)}/month`;
  }, [activeSuite?.price, cart.frequency]);

  const renewalDisplay = useMemo(() => {
    if (!nextPlanDue?.nextBillingTotal) {
      return null;
    }

    if (hasPhases) {
      const nextPhase = nextPlanDue.phases.slice(-1)[0];
      const nextPlan = nextPhase.items.find((item) =>
        item.product.categories?.includes('suite')
      )?.product;
      return join([
        'Downgrade to',
        nextPlan?.name,
        'is scheduled for',
        `${format(nextPhase.endDate, config.dateFormats.monthDay)}.`,
        `\nCharges to ${nextPlanDue.paymentMethodDisplay}`,
      ]);
    }

    if (nextPlanDue.status === 'trialing' && nextPlanDue.trialEnd) {
      return join(
        [
          `Trial ends on ${format(
            nextPlanDue.trialEnd,
            config.dateFormats.monthDayYearLong
          )}`,
          `then, ${renderPrice(nextPlanDue.nextBillingTotal)} charges to ${
            nextPlanDue.paymentMethodDisplay
          }`,
        ],
        config.unicode.centerDotPadded
      );
    }

    return join(
      [
        `Renews on ${format(
          activeSuite?.currentPeriodEnd ??
            nextPlanDue.items[0].currentPeriodEnd,
          config.dateFormats.monthDayYearLong
        )}`,
        `Charges to ${nextPlanDue.paymentMethodDisplay}`,
      ],
      config.unicode.centerDotPadded
    );
  }, [activeSuite?.currentPeriodEnd, hasPhases, nextPlanDue]);

  if (!activeSuite && !suites[0]) {
    return null;
  }

  return (
    <>
      <FlexView direction="column" gap={16} width="100%">
        {activeSuite && (
          <div className={addClassNames(classes.container, className)}>
            <Text as="sectionHeader">Active plan</Text>

            <div className={classes['plan-container']}>
              <FlexView direction="column" fullWidth gap={16}>
                <div>
                  <Text as="subhead2" title={suiteNamesDisplay} truncate={1}>
                    {suiteNamesDisplay}
                  </Text>
                  <Text as="modalHeader">{suiteTotalDisplay}</Text>
                </div>

                {nextPlanDue && (
                  <FlexView gap={4} width="fit-content">
                    <Text
                      as="supportingText"
                      color="TextIconSecondary"
                      tag="pre"
                    >
                      {renewalDisplay}
                    </Text>
                    <Link
                      as="supportingText"
                      className="cursor-pointer"
                      color="TextIconSecondary"
                      display="underline"
                      onClick={() =>
                        changePaymentMethodDrawerRef.current?.open()
                      }
                    >
                      Change payment method
                    </Link>
                  </FlexView>
                )}

                {hasPhases &&
                  (isUncancelingSubscription ? (
                    <Loading type="circle" />
                  ) : (
                    <Link
                      as="supportingText"
                      className="cursor-pointer"
                      display="underline"
                      onClick={() => uncancelSubscription(nextPlanDue)}
                    >
                      Cancel downgrade
                    </Link>
                  ))}
              </FlexView>

              <FlexView
                className={classes['button-container']}
                direction="column"
              >
                {activeSuite ? (
                  <Button
                    color="neutral"
                    content={showPlanOptions ? 'Keep plan' : 'Change plan'}
                    fullWidth
                    onClick={() => togglePlanOptions((prevValue) => !prevValue)}
                    rank="secondary"
                    size="small"
                  />
                ) : (
                  <Button
                    content={showPlanOptions ? 'Nevermind' : 'Subscribe'}
                    fullWidth
                    onClick={() => togglePlanOptions((prevValue) => !prevValue)}
                    rank="secondary"
                    size="small"
                  />
                )}

                {/* <Button
            color="danger"
            content={subscriptions[1] ? 'Cancel all' : 'Cancel'}
            fullWidth
            rank="tertiary"
            size="small"
          /> */}
              </FlexView>
            </div>
          </div>
        )}

        <div
          className={addClassNames(classes['plan-options-container'], {
            [classes.hidden]: activeSuite && !showPlanOptions,
          })}
        >
          <FlexView justify="space-between">
            <Text as="sectionHeader">Plan options</Text>

            {!subscriptionItems[0] && (
              <ToggleButtons<SubscriptionFrequency>
                value={cart.frequency}
                onChange={(value) => {
                  setFrequency(value);
                }}
                options={[
                  {
                    label: 'Monthly',
                    value: 'monthly',
                  },
                  {
                    label: 'Annually',
                    value: 'annual',
                  },
                ]}
              />
            )}
          </FlexView>

          {suites
            .filter((suite) => suite.id !== activeSuite?.product.id)
            .map((suite) => {
              const isActive = activeSuite?.product.sku === suite.sku;
              const frequencyPrice = suite.prices.find(
                (price) => price.frequency === cart.frequency
              );
              if (!frequencyPrice) {
                return null;
              }

              const isUpgrade = isActive
                ? false
                : frequencyPrice.price > (activeSuite?.price.price ?? 0);

              const grade = isUpgrade ? 'Upgrade' : 'Downgrade';
              const changeCta = activeSuite ? grade : 'Select';
              return (
                <div className={classes['plan-container']} key={suite.id}>
                  <FlexView direction="column" fullWidth gap={16}>
                    <div>
                      <Text
                        as="subhead2"
                        title={`${suite.name} ${grade}`}
                        truncate={1}
                      >
                        {suite.name}
                      </Text>
                      <FlexView align="center" gap={8}>
                        <Text as="modalHeader">
                          {renderPrice(frequencyPrice.price)}/{cart.frequency}
                        </Text>
                        {!!frequencyPrice.trialPeriod && (
                          <Tag
                            label={`${frequencyPrice.trialPeriod}-day free trial`}
                            type="positive"
                          />
                        )}
                      </FlexView>
                    </div>

                    <Text as="supportingText" color="TextIconSecondary">
                      {suite.description}
                    </Text>
                  </FlexView>

                  <div className={classes['button-container']}>
                    <Button
                      color={!activeSuite || isUpgrade ? 'action' : 'neutral'}
                      content={changeCta}
                      fullWidth
                      rank="secondary"
                      size="small"
                      onClick={async () => {
                        changeSuite(suite);
                        togglePlanOptions(false);
                      }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </FlexView>

      <SuiteCheckoutDrawer />

      <ChangePaymentMethodFormDrawer
        defaultPaymentMethodId={defaultPaymentMethod?.id}
        organizationId={organizationId}
        ref={changePaymentMethodDrawerRef}
      />
    </>
  );
};
